import { SuccessPageRule } from "../../components/FormClient/FormClient.types";
import { Field, PushData, StepData } from "../../components/FormViewer/FormViewer.types";
import { Insurance, DoctorsResponse } from "../../components/Steps/InputFields/SelectDropdown/SelectDropdown.types";
import { MarketingData } from "../../interfaces";
import { getAppBFFUrl, redirectToSuccessPage, trackEvent } from "../functions";

export async function formatData(lead_data: Array<StepData>, push_data: Array<PushData>, marketing_data: MarketingData, formCompleted?: boolean, multiple_prescriptions?: boolean) {
  const responseBody = new FormData();

  push_data.forEach((el) => {
    const { crm_field, crm_value } = el;
    responseBody.append(crm_field, crm_value);
  });

  lead_data.forEach((step) => {
    const { fields, answer, settings } = step;
    // Get data from Form step types
    fields?.forEach((field: Field) => {
      if (field.field_label === "explicit_consent") {
        // Special case because of timestamp
        field.field_value
          && responseBody.append(
            "timestamp",
            new Date().toISOString().split(".")[0]
          );

      } else if (typeof(field.field_value) === "object" && field.field_value.crm_record_id) {
        responseBody.append(field.field_crm_id, field.field_value.crm_record_id);
      } else if (typeof(field.field_value) === "boolean") {
        responseBody.append(field.field_crm_id, String(field.field_value));
      } else if(field.field_label === "prescriptions" && multiple_prescriptions) {
        [...field.field_value].forEach((file: File)=>{
          responseBody.append(field.field_crm_id, file, file.name);
        });
      } else {
        responseBody.append(field.field_crm_id, field.field_value);
      }
    });

    // Get data from Question step types
    answer && settings?.crm_field && responseBody.append(settings.crm_field, answer.field_value);
  });

  (Object.keys(marketing_data) as (keyof MarketingData)[]).forEach((key) => {
    if (!marketing_data[key] || responseBody.get(key) !== null) return;
    responseBody.append(key, String(marketing_data[key]));
  });

  responseBody.append("is_last_step", String(formCompleted));

  if(window.sessionLayer.lead.id_){
    responseBody.append("id_", window.sessionLayer.lead.id_);
  }

  return responseBody;
}

export const handleStepSuccessEvent = async (response: Pick<Response, "status" | "json">, is_last_step: boolean, hasSuccessStep: boolean, defaultSuccessPageUrl: string, successPageRules: Array<SuccessPageRule>, stepHasEmailField?: boolean, stepHasPhoneField?: boolean) => {
  const { lead: { lead_data} } = window.sessionLayer;

  if (response.status >= 200 && response.status < 300) {

    stepHasEmailField && trackEvent("formClient.partialLeadCreated");
    stepHasPhoneField && trackEvent("formClient.leadCreated");

    if(is_last_step || hasSuccessStep) {
      const eventData = { message: "Lead submitted successfully", leadData: lead_data };
      const event = new CustomEvent('onFcSuccess', { detail: eventData });
      window.dispatchEvent(event);
      trackEvent("formClient.formSubmitted");
      !hasSuccessStep && redirectToSuccessPage(defaultSuccessPageUrl, successPageRules, lead_data);
    } else {
      const eventData = { message: "Step submitted successfully" };
      const event = new CustomEvent('onFcStepTransition', { detail: eventData });
      window.dispatchEvent(event);
    }
  } else {
    // TODO: Send unknown error message
  }
}

export const getInsurances = async (locale: string): Promise<Array<Insurance>> => {
  const baseUrl = await getAppBFFUrl();
  const pathSegment = {
    "fr-FR": "fr",
    "de-DE": "de",
    "en-EN": "de"
  };

  try {
    const res = await fetch(
      `${baseUrl}/platform-nexus/public/v2/insurances/${(pathSegment as any)[locale]}`,
      {  method: 'GET' },
    );

    const jsonData = await res.json();
    const finalData = jsonData.items;
    return finalData;
  } catch (e) {
    // Cors Error or internal server error
    return [] as Array<Insurance>;
  }
}

export const getNearbyZipCodes = async (postalCode: string, radius: number): Promise<Array<string>> => {
  const countryCode = 'DE';
  const url = `https://zip-api.eu/api/v1/radius/${countryCode}-${postalCode}/${radius}/km`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }
    const data = await response.json();
    const postalCodes = data.length ? data.map((el: any) => {
      return el.postal_code;
    }) : [data.postal_code];
    return postalCodes || [];
  } catch (error) {
    console.error('Error fetching data:', error);
    return [];
  }
}

export const getDoctorsWithTotal = async (
  filter: any,
  limit: number,
  skip: number
): Promise<DoctorsResponse> => {
  const baseUrl = await getAppBFFUrl();
  const query = filter ? `?filter=${encodeURIComponent(JSON.stringify(filter))}` : '?';

  const response = await fetch(
    `${baseUrl}/platform-nexus/public/v2/doctors${query}&limit=${limit}&skip=${skip}`,
    { method: 'GET' }
  );

  if (!response.ok) {
    console.error('Error fetching doctors:', response.statusText);
    return { items: [], total: 0 };
  }

  const jsonData = await response.json();
  return {
    items: jsonData.items || [],
    total: jsonData.total || 0,
  };
};

export const handleError = (error: unknown) => {
  return {
    res:
      new Response(null, {
      status: 500
    }),
    details: (error instanceof TypeError || error instanceof Error) ? error?.message : error
  };
};
